<template>
  <v-dialog v-model="dialog" v-if="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card class="exam-dialog">
      <!-- 頂部進度條 -->
      <div class="progress-container">
        <v-btn icon @click="previousStep" v-if="currentStep !== 1">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn @click="closeDialog" color="black" dark v-if="currentStep === 1">
          關閉
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text large :class="{ 'active': currentStep === 1 }" @click="currentStep = 1" class="mr-2 progress-btn">
          面診
        </v-btn>
        <v-btn v-if="examData.firstVisitQuestionExist" text :class="{ 'active': currentStep === 2 }" @click="currentStep = 2" large class="mr-2 progress-btn">
          初診
        </v-btn>
        <v-btn v-if="examData.postOpQuestionExist" text :class="{ 'active': currentStep === 3 }" @click="currentStep = 3" large class="mr-2 progress-btn">
          術後
        </v-btn>
        <v-spacer></v-spacer>
        <template v-if="currentStep === 1">
          <v-btn v-if="examData.firstVisitQuestionExist || examData.postOpQuestionExist" icon @click="nextStep">
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
        <template v-else-if="currentStep === 2">
          <v-btn v-if="examData.postOpQuestionExist" icon @click="nextStep">
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
      </div>

      <!-- 主要內容區域 -->
      <div class="content-container" v-if="currentStep === 1">
        <!-- 左側影片區域 -->
        <div class="video-section">
          <div class="video-placeholder">
            <v-img :src="returnImageUrl(examData.backgroundImage)" contain></v-img>
          </div>
        </div>

        <!-- 右側資訊區域 -->
        <div class="info-section">
          <!-- 病患基本資料 -->
          <div class="patient-info mb-4">
            <div class="d-flex justify-space-between mb-2">
              <div class="info-group">
                <div class="info-label text-left">姓名</div>
                <v-text-field v-model="examData.name" outlined readonly hide-details></v-text-field>
              </div>
              <div class="info-group">
                <div class="info-label text-left">性別</div>
                <v-text-field v-model="examData.gender" outlined readonly hide-details></v-text-field>
              </div>
              <div class="info-group">
                <div class="info-label text-left">年齡</div>
                <v-text-field v-model="examData.age" outlined readonly hide-details></v-text-field>
              </div>
            </div>

            <!-- 主述 -->
            <div class="chief-complaint">
              <div class="info-label text-left">主述</div>
              <v-textarea v-model="examData.description" outlined readonly hide-details rows="4"></v-textarea>
            </div>

            <!-- 任務說明 -->
            <div class="chief-complaint">
              <div class="info-label text-left">任務說明</div>
              <v-textarea v-model="examData.taskDescription" outlined readonly hide-details rows="4"></v-textarea>
            </div>

            <!-- 圖片區域 -->
            <div class="image-section">
              <div class="d-flex align-center mb-2">
                <span class="subtitle-1 font-weight-bold">圖片</span>
              </div>
              <div class="image-slider-container">
                <v-btn v-if="examData.clinicFiles.length > 2" icon class="slider-arrow left" @click="scrollImages('left')">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="image-scroll-container" ref="imageContainer">
                  <div class="image-wrapper" v-for="image in examData.clinicFiles" :key="image">
                    <v-img :src="returnClinicFileUrl(image)" width="180" height="120" cover class="rounded-lg cursor-pointer"
                      @click="openImageDialog(returnClinicFileUrl(image))"></v-img>
                  </div>
                </div>

                <v-btn v-if="examData.clinicFiles.length > 2" icon class="slider-arrow right" @click="scrollImages('right')">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <!-- 對話記錄區域 -->
          <div class="chat-container">
            <div class="messages-container">
              <div v-for="message in studentAnswerData.messages" :key="message._id"
                :class="['message', message.sender_id === 'patient' ? 'message-sent' : 'message-received']">
                {{ message.content }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 初診問答區域 -->
      <div class="content-container" v-if="currentStep === 2">
        <div class="patient-section">
          <v-card class="pa-4">
            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
              <v-text-field v-model="examData.name" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">性別</div>
              <v-text-field v-model="examData.gender" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
              <v-text-field v-model="examData.age" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">主述</div>
              <v-textarea v-model="examData.description" outlined readonly auto-grow rows="4"></v-textarea>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
              <v-textarea v-model="examData.taskDescription" outlined readonly auto-grow rows="4"></v-textarea>
            </div>

            <!-- 初診圖片 -->
            <div class="image-section">
              <div class="d-flex align-center mb-2">
                <span class="subtitle-1 font-weight-bold">圖片</span>
              </div>
              <div class="image-slider-container">
                <v-btn v-if="examData.firstVisitFiles.length > 2" icon class="slider-arrow left">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="image-scroll-container">
                  <div class="image-wrapper" v-for="image in examData.firstVisitFiles" :key="image">
                    <v-img :src="returnFirstVisitFileUrl(image)" width="180" height="120" cover
                      class="rounded-lg cursor-pointer"></v-img>
                  </div>
                </div>

                <v-btn v-if="examData.firstVisitFiles.length > 2" icon class="slider-arrow right">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>

        <!-- 初診問答 -->
        <div class="exam-form-section">
          <v-card class="pa-4">
            <div class="questions-answer-container">
              <div class="questions-list">
                <div v-for="(question, index) in examData.firstVisitQuestions" :key="index" class="nav-item"
                  :class="{ active: currentFirstVisitQuestionIndex === index }" @click="currentFirstVisitQuestionIndex = index">
                  題目{{ index + 1 }}
                </div>
              </div>

              <div class="answer-container">
                <div class="question-title mb-4">問題</div>
                <div class="question-text mb-6">
                  {{ examData.firstVisitQuestions[currentFirstVisitQuestionIndex]?.title }}
                </div>
                <div class="answer-section">
                  <div class="answer-label mb-2">學生回答</div>
                  {{ studentAnswerData.answers.firstVisitQuestions[currentFirstVisitQuestionIndex].answer }}
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>

      <!-- 術後問答區域 -->
      <div class="content-container" v-if="currentStep === 3">
        <div class="patient-section">
          <v-card class="pa-4">
            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
              <v-text-field v-model="examData.name" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">性別</div>
              <v-text-field v-model="examData.gender" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
              <v-text-field v-model="examData.age" outlined dense readonly></v-text-field>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">情境說明</div>
              <v-textarea v-model="examData.description" outlined readonly auto-grow rows="4"></v-textarea>
            </div>

            <div class="mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
              <v-textarea v-model="examData.taskDescription" outlined readonly auto-grow rows="4"></v-textarea>
            </div>

            <!-- 術後圖片 -->
            <div class="image-section">
              <div class="d-flex align-center mb-2">
                <span class="subtitle-1 font-weight-bold">圖片</span>
              </div>
              <div class="image-slider-container">
                <v-btn v-if="examData.postOpFiles.length > 2" icon class="slider-arrow left">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="image-scroll-container">
                  <div class="image-wrapper" v-for="image in examData.postOpFiles" :key="image">
                    <v-img :src="returnPostOpFileUrl(image)" width="180" height="120" cover
                      class="rounded-lg cursor-pointer"></v-img>
                  </div>
                </div>

                <v-btn v-if="examData.postOpFiles.length > 2" icon class="slider-arrow right">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>

        <!-- 術後問答 -->
        <div class="exam-form-section">
          <v-card class="pa-4">
            <div class="questions-answer-container">
              <div class="questions-list">
                <div v-for="(question, index) in examData.postOpQuestions" :key="index" class="nav-item"
                  :class="{ active: currentPostOpQuestionIndex === index }" @click="currentPostOpQuestionIndex = index">
                  題目{{ index + 1 }}
                </div>
              </div>

              <div class="answer-container">
                <div class="question-title mb-4">問題</div>
                <div class="question-text mb-6">
                  {{ examData.postOpQuestions[currentPostOpQuestionIndex]?.title }}
                </div>
                <div class="answer-section">
                  <div class="answer-label mb-2">學生回答</div>
                  {{ studentAnswerData.answers.postOpQuestions[currentPostOpQuestionIndex].answer }}
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </v-card>

    <!-- 圖片放大對話框 -->
    <v-dialog v-model="showImageDialog">
      <v-card>
        <v-card-actions class="d-flex justify-end pa-2">
          <v-btn icon @click="showImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-img :src="selectedImage" contain height="100%" width="100%"></v-img>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import baseUrl from '../../baseUrl.config';

export default {
  name: 'ExamAnswerViewer',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    examResult: {
      type: Object,
      required: true
    },
    studentAnswer: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      currentStep: 1,
      showImageDialog: false,
      selectedImage: '',
      currentFirstVisitQuestionIndex: 0,
      currentPostOpQuestionIndex: 0,
      examData: {
            name: "",
        gender: "",
        age: "",
        description: "",
        taskDescription: "",
        backgroundImage: "",
        clinicFiles: [],
        firstVisitFiles: [],
        postOpFiles: [],
        firstVisitQuestionExist: true,
        postOpQuestionExist: true,
        student_id: "student123",
        messages: [
        ],
        firstVisitQuestions: [
        ],
        postOpQuestions: [
        ]
      },
      studentAnswerData: {
        messages: [],
        answers:[],
      }
    }
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
    },
    dialog(newVal) {
      this.$emit('input', newVal);
    },
    examResult: {
      handler(newVal) {
        if (newVal) {
          this.examData = { ...newVal };
        }
      },
      immediate: true
    },
    studentAnswer: {
      handler(newVal) {
        if (newVal) {
          this.studentAnswerData = { ...newVal };
        }
      },
      immediate: true
    }
  },
  computed: {
    currentFirstVisitAnswer: {
      get() {
        return this.examData.firstVisitQuestions[this.currentFirstVisitQuestionIndex]?.answer || '';
      }
    },
    currentPostOpAnswer: {
      get() {
        return this.examData.postOpQuestions[this.currentPostOpQuestionIndex]?.answer || '';
      }
    }
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },
    scrollImages(direction) {
      const container = this.$refs.imageContainer;
      const scrollAmount = 180 + 16;
      if (direction === 'left') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    },
    openImageDialog(imageUrl) {
      this.selectedImage = imageUrl;
      this.showImageDialog = true;
    },
    returnImageUrl(imageUrl) {
      return baseUrl + "uploadFiles/backgroundImage/" + imageUrl;
    },
    returnClinicFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/clinicFiles/" + imageUrl;
    },
    returnFirstVisitFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/firstVisitFiles/" + imageUrl;
    },
    returnPostOpFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/postOpFiles/" + imageUrl;
    }
  }
}
</script>

<style scoped>
.exam-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f8f8f8;
  overflow: hidden;
}

.progress-container {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background-color: white;
  border-bottom: 1px solid #eee;
}

.content-container {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  padding: 24px;
  height: calc(100vh - 64px);
  overflow: hidden;
}

.video-section {
  flex: 1;
  background-color: black;
  border-radius: 8px;
  overflow: hidden;
  height: 320px;
}

.info-section {
  width: 60vw;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
}

.patient-info {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
}

.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-top: 20px;
}

.messages-container {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.message {
  max-width: 70%;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  word-break: break-word;
}

.message-sent {
  align-self: flex-end;
  background-color: #000;
  color: white;
}

.message-received {
  align-self: flex-start;
  background-color: #f5f5f5;
  color: #333;
}

.image-slider-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 40px;
}

.image-scroll-container {
  display: flex;
  overflow-x: hidden;
  scroll-behavior: smooth;
  width: 100%;
  gap: 16px;
  padding: 8px 0;
}

.questions-answer-container {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  padding-top: 24px;
}

.questions-list {
  border-right: 1px solid #e0e0e0;
  padding-right: 16px;
}

.nav-item {
  padding: 12px 16px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.nav-item.active {
  color: #000;
  font-weight: 500;
}

.progress-btn.active {
  background-color: #000 !important;
  color: white !important;
}

.progress-btn {
  min-width: 80px;
}
</style> 